import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { ConsentService } from './consent.service';
import { SeoApiService } from './seo-api.service';

type Lintrk = ((...args: unknown[]) => void) & {
  q?: unknown[][];
};

@Injectable({ providedIn: 'root' })
export class LinkedInInsightService {
  private lastPath = '';
  private started = false;
  private scriptLoaded = false;
  private partnerId = '';

  constructor(
    private readonly router: Router,
    private readonly consent: ConsentService,
    private readonly seoApi: SeoApiService,
  ) {}

  start(): void {
    if (this.started || !this.consent.hasConsent()) {
      return;
    }
    this.started = true;
    this.seoApi.publico().subscribe((config) => {
      const id = config.rastreamento.linkedin_partner_id?.trim() ?? '';
      if (!id || !this.consent.hasConsent()) {
        return;
      }
      this.partnerId = id;
      this.garantirScript();
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => this.onNavigate(event.urlAfterRedirects));

      if (this.router.navigated) {
        this.onNavigate(this.router.url);
      }
    });
  }

  private onNavigate(url: string): void {
    if (!this.consent.hasConsent() || !this.partnerId) {
      return;
    }
    const path = this.pathOf(url);
    if (path === this.lastPath) {
      return;
    }
    this.lastPath = path;
    this.lintrk('track');
  }

  private garantirScript(): void {
    if (typeof document === 'undefined' || this.scriptLoaded || !this.partnerId) {
      return;
    }
    const w = window as Window & {
      _linkedin_partner_id?: string;
      _linkedin_data_partner_ids?: string[];
      lintrk?: Lintrk;
    };
    this.scriptLoaded = true;
    w._linkedin_partner_id = this.partnerId;
    w._linkedin_data_partner_ids = w._linkedin_data_partner_ids || [];
    w._linkedin_data_partner_ids.push(this.partnerId);
    if (typeof w.lintrk !== 'function') {
      const lintrk: Lintrk = function (...args: unknown[]) {
        (lintrk.q = lintrk.q || []).push(args);
      };
      w.lintrk = lintrk;
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
    document.head.appendChild(script);
  }

  private pathOf(url: string): string {
    const path = url.split('?')[0].split('#')[0];
    if (path.length > 1 && path.endsWith('/')) {
      return path.slice(0, -1);
    }
    return path || '/';
  }

  private lintrk(...args: unknown[]): void {
    const fn = (window as Window & { lintrk?: Lintrk }).lintrk;
    if (typeof fn === 'function') {
      fn(...args);
    }
  }
}
